import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, TextField, Box, Grid, Typography, Checkbox, FormControlLabel, Collapse, Divider, CardContent, Card, IconButton } from "@mui/material";
import PageLayout from "../../atoms/page-layout";
import ExpertsListPageHeader from "../../molecules/app-bars/ExpertsList-Enrichment";
import { fetchExpertData, updateExpertData } from "./helper";
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExperienceForm from "./experience/experienceForm";
import EducationForm from "../../organisms/edit-expert/education/education-form";
import { educationCompareKeys, EXPERT_API_RESPONSES, workExpCompareKeys } from "./expert-list.constants";
import { useSnackbar } from "notistack";
import FullPageReview from "./fullPageReview";
import CheckIcon from "@mui/icons-material/Check";
import CardsLoadingScreen from "../../atoms/cardsLoader";
import dayjs from "dayjs";
import { ContentCopy } from "@mui/icons-material";

interface WorkExperience {
    id?: number;
    company: string;
    location?: string;
    fk_company?: number;
    designation: string;
    job_description: string;
    division: string;
    start_date: string | null;
    end_date: string | null;
    currently_works_here: boolean;
    initial_company?: string;
}

interface Education {
    course: string;
    start_year: string;
    end_year: string;
    institution: string;
}

interface ExpertData {
    existingData: {
        name: string;
        email: string;
        headline?: string;
        work_experiences: WorkExperience[];
        education: Education[];
    };
    enrichedData: {
        name: string;
        email: string;
        headline?: string;
        work_experiences: WorkExperience[];
        education: Education[];
    };
}

const generateRefs = (rows: number, columns: number) =>
    Array.from({ length: rows }, () =>
        Array.from({ length: columns }, () => React.createRef<any>())
    );

const ExpertsListPage = () => {
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchQueryConst, setSearchQueryConst] = useState("");
    const [searchParams, setSearchParams] = useSearchParams();
    const expertId = parseInt(searchParams.get("id") || "nan");
    const linkedin_url = searchParams.get("link");
    const [responseData, setResponseData] = useState<ExpertData | null>(null);
    const [localIdentityData, setLocalIdentityData] = useState<any>({ existingData: { name: "", headline: "" }, enrichedData: { name: "", headline: "" } });
    const finalWorkDataRef = useRef<any[][]>([]);
    let finalWorkData = finalWorkDataRef.current;
    useEffect(() => {
        finalWorkData = finalWorkDataRef.current;
    }, [finalWorkDataRef.current]);
    const setFinalWorkData = (newData: any[][]) => {
        finalWorkDataRef.current = newData;
    };
    useEffect(() => {
        if (!searchQuery || searchQuery.trim() === "") {
            return;
        }
        handleSubmit();
    }, [searchQuery])
    const [isWorkExperienceEditable, setIsWorkExperienceEditable] = useState<boolean[][]>([]);
    const [finalEducationData, setFinalEducationData] = useState<any[][]>([]);
    const [isWorkExperienceCollapsed, setIsWorkExperienceCollapsed] = useState(false);
    const [isEducationCollapsed, setIsEducationCollapsed] = useState(false);
    const [isBasicInfoCollapsed, setIsBasicInfoCollapsed] = useState(false);
    const [isEducationEditable, setIsEducationEditable] = useState<boolean[][]>([]);
    const [isExistingHeadlineEditable, setIsExistingHeadlineEditable] = useState(true);
    const [showReviewPage, setShowReviewPage] = useState<boolean>(false);
    const [payload, setPayload] = useState<any>({});
    const [errorFromValidations, setErrorFromValidations] = useState<Record<string, Record<string, string>[][]>>({});
    const [toggleReRender, setToggleReRender] = useState<number>(0);
    const formComponentRefsRef = useRef<Record<string, any>>({});
    let formComponentRefs = formComponentRefsRef.current;
    useEffect(() => {
        formComponentRefs = formComponentRefsRef.current;
    }, [formComponentRefsRef.current]);
    const setFormComponentRefs = (newRefs: Record<string, any>) => {
        formComponentRefsRef.current = newRefs;
    };
    // const [formComponentRefs, setFormComponentRefs] = useState<Record<string, any>>({});
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    useEffect(() => {
        setSearchQuery(linkedin_url || "");
        if (!expertId) {
            navigate(-1);
        }
    }, [expertId, linkedin_url])
    const forceReRender = async () => {
        setToggleReRender((prev) => prev + 1);
    }
    const getWorkEditableStatus = (index: number, i: number): boolean => {
        if (isWorkExperienceEditable.length <= index || isWorkExperienceEditable[index].length < 2) {
            return false;
        }
        return isWorkExperienceEditable[index][i];
    }
    const getEducationEditableStatus = (index: number, i: number): boolean => {
        if (isEducationEditable.length <= index || isEducationEditable[index].length < 2) {
            return false;
        }
        return isEducationEditable[index][i];
    }
    const toggleEducationEditable = (index: number, i: number) => {
        setIsEducationEditable((prev) => {
            const newState = [...prev];
            newState[index][i] = !newState[index][i];
            return newState;
        });
    };
    const toggleWorkExperienceEditable = (index: number, i: number) => {
        setIsWorkExperienceEditable((prev) => {
            const newState = [...prev];
            newState[index][i] = !newState[index][i];
            return newState;
        });
    };
    const parseNewData = (data: any, newData: any): any => {
        const keys = Object.keys(newData);
        const newParsedData = { ...data };
        for (let key of keys) {
            newParsedData[key] = newData[key];
        }
        return newParsedData;
    }
    function formatDate(date: any): string {
        const year = date?.$y;
        const month = String(date?.$M + 1).padStart(2, '0'); // Months are 0-indexed
        return `${year}-${month}`;
    }
    const parseNewWorkData = (data: WorkExperience, newData: any): WorkExperience => {
        const newParsedData = { ...newData };
        newParsedData.company = data?.company;
        newParsedData.fk_company = data?.fk_company;
        if (newData?.company?.value) {
            newParsedData.company = newData?.company.label;
            newParsedData.fk_company = newData?.company.value;
        }
        if (!newData?.company) {
            newParsedData.company = null;
            newParsedData.fk_company = undefined;
        }
        if (typeof newData?.location !== "string") {
            newParsedData.location = newData?.location?.label;
        }
        newParsedData.start_date = newData?.start_date ? formatDate(newData?.start_date) : undefined;
        newParsedData.end_date = newData?.end_date ? formatDate(newData?.end_date) : undefined;
        return parseNewData(data, newParsedData);
    }
    const handleLocalWorkDataChange = (index: number, i: number, value: any) => {
        const newData = [...finalWorkData];
        const finalParsedData = parseNewWorkData(newData[index][i], value);
        newData[index][i] = finalParsedData;
        setFinalWorkData(newData);
    }
    const parseNewEduData = (data: Education, newData: any): Education => {
        const newParsedData = { ...newData };
        newParsedData.start_year = newData?.start_year ? `${newData.start_year.$y}` : undefined;
        newParsedData.end_year = newData?.end_year ? `${newData.end_year.$y}` : undefined;
        return parseNewData(data, newParsedData);
    }
    const handleLocalEduDataChange = (index: number, i: number, value: any) => {
        const newData = [...finalEducationData];
        const finalParsedData = parseNewEduData(newData[index][i], value);
        newData[index][i] = finalParsedData;
        setFinalEducationData(newData);
    }
    const handleLocalExistingHeadlineChange = async (e: any) => {
        if (isExistingHeadlineEditable) {
            setLocalIdentityData({
                ...localIdentityData,
                existingData: {
                    ...localIdentityData?.existingData,
                    headline: e?.target?.value,
                },
            });
        }
    }
    const handleLocalEnrichedHeadlineChange = async (e: any) => {
        if (!isExistingHeadlineEditable) {
            setLocalIdentityData({
                ...localIdentityData,
                enrichedData: {
                    ...localIdentityData?.enrichedData,
                    headline: e?.target?.value,
                },
            });
        }
    }
    const removeSpecificValidationError = (index: number, i: number, key: string, field: string) => {
        setErrorFromValidations((prev) => {
            let newErrors = prev?.[key]?.[index]?.[i] ? prev[key][index][i] : {};
            delete newErrors?.[field];
            let newValidationErrors = prev;
            newValidationErrors[key][index][i] = newErrors;
            return newValidationErrors;
        });
        forceReRender();
    }
    const setValidationErrors = (errors: Record<string, string>, index: number, i: number, key: string) => {
        setErrorFromValidations((prev) => {
            let newErrors = prev?.[key]?.[index]?.[i] ? prev[key][index][i] : {};
            newErrors = { ...newErrors, ...errors };
            let newValidationErrors = prev;
            newValidationErrors[key][index][i] = newErrors;
            return newValidationErrors;
        });
        forceReRender();
    }
    const workExpErrorHandler = (work: WorkExperience, index: number, i: number): boolean => {
        let errors: Record<string, string> = {}
        let valid = true;
        if (!work?.company || work?.company?.trim() === "" || !work?.fk_company) {
            errors[`company`] = EXPERT_API_RESPONSES.COMPANY_IS_REQUIRED;
            valid = false;
        }
        if (!work?.designation || work?.designation?.trim() === "") {
            errors[`designation`] = EXPERT_API_RESPONSES.DESIGNATION_IS_REQUIRED;
            valid = false;
        }
        if (!work?.location || work?.location?.trim() === "") {
            errors[`location`] = EXPERT_API_RESPONSES.LOCATION_IS_REQUIRED;
            valid = false;
        }

        if (!work?.start_date) {
            errors[`start_date`] = EXPERT_API_RESPONSES.START_DATE_IS_REQUIRED;
            valid = false;
        }
        if (typeof work?.currently_works_here !== "boolean") {
            errors[`currworkshere`] = EXPERT_API_RESPONSES.CURRENTLY_WORKS_HERE_IS_REQUIRED;
            valid = false;
        }
        if (!work?.currently_works_here && !work?.end_date) {
            errors[`end_date`] = EXPERT_API_RESPONSES.END_DATE_IS_REQUIRED;
            valid = false;
        }
        setValidationErrors(errors, index, i, "work");
        return valid;
    }
    const educationErrorHandler = (edu: Education, index: number, i: number): boolean => {
        let errors: Record<string, string> = {}
        let valid = true;
        if (!edu.course || edu.course.trim() === "") {
            errors[`course`] = EXPERT_API_RESPONSES.COURSE_IS_REQUIRED;
            valid = false;
        }
        if (!edu.institution || edu.institution.trim() === "") {
            errors[`institution`] = EXPERT_API_RESPONSES.INSTITUTION_IS_REQUIRED;
            valid = false;
        }
        if (!edu.start_year) {
            errors[`start_year`] = EXPERT_API_RESPONSES.START_YEAR_IS_REQUIRED;
            valid = false;
        }
        if (!edu.end_year) {
            errors[`end_year`] = EXPERT_API_RESPONSES.END_YEAR_IS_REQUIRED;
            valid = false;
        }
        setValidationErrors(errors, index, i, "edu");
        return valid;
    }

    const handleSubmit = async () => {
        if (!searchQuery) {
            enqueueSnackbar(EXPERT_API_RESPONSES.PLEASE_ENTER_URL, {
                variant: "error",
                autoHideDuration: 2000,
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
            });
            return;
        }
        try {
            setLoading(true);
            const data = await fetchExpertData(searchQuery, expertId);
            if (data) {
                setResponseData(data);

            } else {
                enqueueSnackbar(EXPERT_API_RESPONSES.FAILED_TO_FETCH_DATA, {
                    variant: "error",
                    autoHideDuration: 2000,
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                });
            }
        } catch (error) {
            enqueueSnackbar(EXPERT_API_RESPONSES.ERROR_OCCURRED_WHILE_FETCHING_DATA, {
                variant: "error",
                autoHideDuration: 2000,
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
            });
        } finally {
            setLoading(false);
            setSearchQueryConst(searchQuery);
        }
    };

    const handleUpdate = async () => {
        try {
            setLoading(true);
            let firstError = undefined;
            let finalWorkExperiencesPayload = [];
            let finalEducationPayload = [];
            let valid = true;
            for (let index = 0; index < finalWorkData.length; index++) {
                const work = finalWorkData[index];
                if (work[0] && isWorkExperienceEditable[index][0]) {
                    if (!workExpErrorHandler(work[0], index, 0)) {
                        valid = false;
                        if (!firstError) {
                            firstError = formComponentRefs?.work?.[index]?.[0];
                        }
                    };
                    finalWorkExperiencesPayload.push(work[0]);
                }
                if (work[1] && isWorkExperienceEditable[index][1]) {
                    if (!workExpErrorHandler(work[1], index, 1)) {
                        valid = false;
                        if (!firstError) {
                            firstError = formComponentRefs?.work?.[index]?.[0];
                        }
                    };
                    finalWorkExperiencesPayload.push(work[1]);
                }
            }
            for (let index = 0; index < finalEducationData.length; index++) {
                const edu = finalEducationData[index];
                if (edu[0] && isEducationEditable[index][0]) {
                    if (!educationErrorHandler(edu[0], index, 0)) {
                        valid = false;
                        if (!firstError) {
                            firstError = formComponentRefs?.edu?.[index]?.[0];
                        }
                    }
                    finalEducationPayload.push(edu[0]);
                }
                if (edu[1] && isEducationEditable[index][1]) {
                    if (!educationErrorHandler(edu[1], index, 1)) {
                        valid = false;
                        if (!firstError) {
                            firstError = formComponentRefs?.edu?.[index]?.[1];
                        }
                    }
                    finalEducationPayload.push(edu[1]);
                }
            }
            if (!valid) {
                enqueueSnackbar("There are Some Errors", {
                    variant: "error",
                    autoHideDuration: 2000,
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                });
                firstError?.current?.scrollIntoView({ behavior: "smooth" });
                return;
            }
            for (let index = 0; index < finalWorkExperiencesPayload.length; index++) {
                delete finalWorkExperiencesPayload[index]?.initial_company
                delete finalWorkExperiencesPayload[index]?.relevant
            }
            let name = localIdentityData?.existingData?.name;
            let headline = localIdentityData?.existingData?.headline;
            if (!isExistingHeadlineEditable) {
                headline = localIdentityData?.enrichedData?.headline;
            }
            const payload = {
                name: name,
                expertId: expertId,
                headline: headline,
                source_link: linkedin_url,
                education: finalEducationPayload,
                work_experiences: finalWorkExperiencesPayload,
            };
            setPayload(payload);
            setShowReviewPage(true);
        } catch (error) {
            console.log(error);
            enqueueSnackbar(EXPERT_API_RESPONSES.ERROR_OCCURED_WHILE_UPDATING_DATA, {
                variant: "error",
                autoHideDuration: 2000,
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
            });
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        if (responseData) {
            handleInitializeLocalData();
            workExperienceParser();
            educationParser();
        }
    }, [responseData]);

    const handleInitializeLocalData = () => {
        const localData = {
            existingData: {
                name: responseData?.existingData?.name,
                headline: responseData?.existingData?.headline,
            },
            enrichedData: {
                name: responseData?.enrichedData?.name,
                headline: responseData?.enrichedData?.headline,
            }
        }
        setLocalIdentityData(localData);
    }

    const compareWithKeys = (a: any, b: any, keys: string[]): boolean => {
        for (let key of keys) {
            if (typeof a?.[key] === "string" && typeof b?.[key] === "string") {
                if (a?.[key]?.trim()?.toLowerCase() !== b?.[key]?.trim()?.toLowerCase()) {
                    return false;
                } else {
                    continue;
                }
            }
            if (a?.[key] !== b?.[key]) {
                return false;
            }
        }
        return true;
    }
    const compareDate = (a: string | null, b: string | null): boolean => {
        if (!a || !b) {
            return !!(!a && b);
        }
        return new Date(a) > new Date(b);
    }
    const workExperienceParser = () => {
        let existingWorkExp: WorkExperience[] = responseData?.existingData?.work_experiences || [];
        let enrichedWorkExp: WorkExperience[] = responseData?.enrichedData?.work_experiences || [];
        let finalData: any[][] = [];
        let left = 0;
        let right = 0;
        for (let index = 0; index < enrichedWorkExp.length; index++) {
            enrichedWorkExp[index].initial_company = enrichedWorkExp[index]?.company;
        }
        while (left < existingWorkExp.length && right < enrichedWorkExp.length) {
            if (compareWithKeys(existingWorkExp[left], enrichedWorkExp[right], workExpCompareKeys)) {
                finalData.push([existingWorkExp[left], enrichedWorkExp[right]]);
                left++;
                right++;
            } else {
                if (compareDate(existingWorkExp[left]?.start_date, enrichedWorkExp[right]?.start_date)) {
                    finalData.push([existingWorkExp[left], null]);
                    left++;
                } else {
                    finalData.push([null, enrichedWorkExp[right]]);
                    right++;
                }
            }
        }
        while (left < existingWorkExp.length) {
            finalData.push([existingWorkExp[left], null]);
            left++;
        }
        while (right < enrichedWorkExp.length) {
            finalData.push([null, enrichedWorkExp[right]]);
            right++;
        }
        const workEditableFalseData = Array(finalData.length);
        for (let index = 0; index < finalData.length; index++) {
            for (let i = 0; i <= 1; i++) {
                if (!finalData[index][i]) {
                    continue;
                }
                finalData[index][i].start_date = finalData[index][i].start_date ? formatDate(dayjs(finalData[index][i]?.start_date)) : null;
                finalData[index][i].end_date = finalData[index][i]?.end_date ? formatDate(dayjs(finalData[index][i]?.end_date)) : null;
            }
        }
        for (let index = 0; index < finalData.length; index++) {
            const newFalsePair = [false, false];
            workEditableFalseData[index] = newFalsePair;
        }
        const workValidationErrors = Array(finalData.length);
        for (let index = 0; index < finalData.length; index++) {
            const errorObject = [{}, {}];
            workValidationErrors[index] = errorObject;
        }
        setErrorFromValidations((prev) => { return { ...prev, work: workValidationErrors } });
        const workFormRefs = generateRefs(finalData.length, 2);
        setFormComponentRefs({ ...formComponentRefs, work: workFormRefs });
        // setFormComponentRefs((prev) => { return { ...prev, work: workFormRefs } });
        setIsWorkExperienceEditable(workEditableFalseData);
        setFinalWorkData(finalData);
    };
    const educationParser = () => {
        let existingEducation: Education[] = responseData?.existingData?.education || [];
        let enrichedEducation: Education[] = responseData?.enrichedData?.education || [];
        let finalData: any[][] = [];
        let left = 0;
        let right = 0;

        while (left < existingEducation.length && right < enrichedEducation.length) {
            if (compareWithKeys(existingEducation[left], enrichedEducation[right], educationCompareKeys)) {
                finalData.push([existingEducation[left], enrichedEducation[right]]);
                left++;
                right++;
            } else {
                if (compareDate(existingEducation[left]?.start_year, enrichedEducation[right]?.start_year)) {
                    finalData.push([existingEducation[left], null]);
                    left++;
                } else {
                    finalData.push([null, enrichedEducation[right]]);
                    right++;
                }
            }
        }

        while (left < existingEducation.length) {
            finalData.push([existingEducation[left], null]);
            left++;
        }

        while (right < enrichedEducation.length) {
            finalData.push([null, enrichedEducation[right]]);
            right++;
        }
        const educationEditableFalseData = Array(finalData.length);
        for (let index = 0; index < finalData.length; index++) {
            const newFalsePair = [false, false];
            educationEditableFalseData[index] = newFalsePair;
        }
        const eduValidationErrors = Array(finalData.length);
        for (let index = 0; index < finalData.length; index++) {
            const errorObject = [{}, {}];
            eduValidationErrors[index] = errorObject;
        }
        setErrorFromValidations((prev) => { return { ...prev, edu: eduValidationErrors } });
        const eduFormRefs = generateRefs(finalData.length, 2);
        setFormComponentRefs({ ...formComponentRefs, work: eduFormRefs });
        // setFormComponentRefs((prev) => { return { ...prev, work: eduFormRefs } });
        setIsEducationEditable(educationEditableFalseData);
        setFinalEducationData(finalData);
    };
    const renderWorkExperience = (workArray: any[], index: number, i: number) => {
        const work = workArray[i];
        if (!work) {
            return (
                <>
                </>)
        }
        return (
            <div ref={(node) => {
                if (node) {
                    // Ensure the ref is stored in your refs array
                    if (formComponentRefs?.current?.work?.[index]?.[i]) {
                        formComponentRefs.current.work[index][i].current = node;
                    }
                }
            }}>
                <Box sx={{ marginTop: 1 }} key={index} >
                    <Card sx={{
                        boxShadow: 3, marginBottom: 0, borderRadius: '25px',
                        border: getWorkEditableStatus(index, i) ? '2px solid #ef9e48' : 'none',

                    }}>
                        <CardContent sx={{ pb: "0px !important" }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ position: 'relative' }}>
                                {i === 1 ? (
                                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                                        <Typography variant="body1" sx={{ fontWeight: 500, marginLeft: 2 }}>
                                            Company: <b>{work?.initial_company}</b>
                                        </Typography>
                                        <IconButton sx={{
                                            height: "30px",
                                            width: "30px",
                                        }} onClick={() => handleCompanyNameCopy(work?.initial_company)}>
                                            <ContentCopy sx={{ height: "17px", width: "17px" }} />
                                        </IconButton>
                                    </div>
                                ) : <Typography></Typography>}
                                <IconButton
                                    sx={{
                                        // position: 'absolute',
                                        // top: '10px',
                                        // right: '10px',
                                        color: getWorkEditableStatus(index, i) ? "#fff" : "#37B498",
                                        backgroundColor: getWorkEditableStatus(index, i)
                                            ? "#37B498"
                                            : "rgba(55, 180, 152, 0.12)",
                                        height: "30px",
                                        width: "30px",
                                        borderRadius: "10px",
                                        "&:hover": {
                                            backgroundColor: getWorkEditableStatus(index, i)
                                                ? "#37B498"
                                                : "rgba(55, 180, 152, 0.12)",
                                        },
                                    }}
                                    onClick={() => toggleWorkExperienceEditable(index, i)}
                                >
                                    <CheckIcon />
                                </IconButton>
                            </Box>
                            <Box>
                                <ExperienceForm
                                    defaultValues={{
                                        company: i === 1 ? "" : work?.company,
                                        designation: work?.designation,
                                        location: work?.location || null,
                                        division: work?.division,
                                        currently_works_here: work?.currently_works_here,
                                        job_description: work?.job_description,
                                        start_date: work?.start_date ? new Date(work?.start_date) : null,
                                        end_date: work?.end_date ? new Date(work?.end_date) : null,
                                    }}
                                    setOnChange={(value: any) => { handleLocalWorkDataChange(index, i, value) }}
                                    index={index}
                                    showButtons={false}
                                    isEditable={getWorkEditableStatus(index, i)}
                                    validationErrors={errorFromValidations?.work?.[index]?.[i] || null}
                                    removeSpecificValidationError={(field: string) => { removeSpecificValidationError(index, i, "work", field) }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </div>
        )
    };
    const renderEducation = (eduArray: any[], index: number, columnIndex: number) => {
        const edu = eduArray[columnIndex];
        if (!edu) {
            return <Box key={index}></Box>;
        }
        return (
            <div ref={formComponentRefs?.edu?.[index]?.[columnIndex]}>
                <Box key={index} sx={{ marginTop: 1 }}>
                    <Card
                        sx={{
                            boxShadow: 3,
                            marginBottom: 0,
                            borderRadius: '25px',
                            border: getEducationEditableStatus(index, columnIndex) ? '2px solid #ef9e48' : 'none',
                        }}>
                        <CardContent sx={{ pb: "0px !important" }}>
                            <Box display="flex" alignItems="flex-start" sx={{ position: 'relative' }}>
                                <IconButton
                                    sx={{
                                        position: 'absolute',
                                        top: '10px',
                                        right: '10px',
                                        color: getEducationEditableStatus(index, columnIndex) ? "#fff" : "#37B498",
                                        backgroundColor: getEducationEditableStatus(index, columnIndex)
                                            ? "#37B498"
                                            : "rgba(55, 180, 152, 0.12)",
                                        height: "30px",
                                        width: "30px",
                                        borderRadius: "10px",
                                        "&:hover": {
                                            backgroundColor: getEducationEditableStatus(index, columnIndex)
                                                ? "#37B498"
                                                : "rgba(55, 180, 152, 0.12)",
                                        },
                                    }}
                                    onClick={() => toggleEducationEditable(index, columnIndex)}
                                >
                                    <CheckIcon />
                                </IconButton>

                            </Box>
                            <Box sx={{ marginTop: '25px' }}>
                                <EducationForm
                                    defaultValues={{
                                        course: edu?.course,
                                        institution: edu?.institution,
                                        start_year: edu?.start_year,
                                        end_year: edu?.end_year,
                                    }}
                                    index={index}
                                    setOnChange={(value: any) => { handleLocalEduDataChange(index, columnIndex, value) }}
                                    showButtons={false}
                                    minSpacing={true}
                                    isEditable={getEducationEditableStatus(index, columnIndex)}
                                    validationErrors={errorFromValidations?.edu?.[index]?.[columnIndex] || null}
                                    removeSpecificValidationError={(field: string) => { removeSpecificValidationError(index, columnIndex, "edu", field) }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </div>
        );
    };
    const copyToClipboard = (text: string) => {
        var textField = document.createElement('textarea')
        textField.innerText = text
        document.body.appendChild(textField)
        textField.select()
        document.execCommand('copy')
        textField.remove()
    }
    const handleCompanyNameCopy = async (name: string) => {
        copyToClipboard(name);
    }
    return (
        <PageLayout>
            <ExpertsListPageHeader
                setFilterPayload={(callback) => {
                    const newSearchParams = new URLSearchParams(searchParams?.toString());
                    callback(newSearchParams);
                    setSearchParams(newSearchParams);
                }}
            />
            {showReviewPage && <FullPageReview
                closeReview={() => { setShowReviewPage(false) }}
                payload={payload}
            />}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0px', paddingBottom: "0px" }}>
                <div style={{ display: 'flex', gap: "1rem" }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, marginBottom: 2 }}>
                        Expert ID : <b>{expertId}</b>
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, marginBottom: 2 }}>
                        Expert Name : <b>{localIdentityData?.existingData?.name}</b>
                    </Typography>
                </div>
                <Typography variant="body2" sx={{ fontWeight: 500, marginBottom: 2, cursor: "Pointer" }} onClick={() => { window.open(searchQueryConst, '_blank'); }}>
                    Linkedin URL : <b style={{ color: "#1dadc0", textDecoration: "underline" }}>{searchQueryConst}</b>
                </Typography>
            </div>
            <div style={{ display: 'flex', width: "calc(100% - 3rem)", marginBottom: '0px', paddingBottom: "0px", marginLeft: "1rem", marginRight: "calc(2rem + 2px)", gap: "2rem" }}>
                <Typography variant="h6" sx={{ backgroundColor: '#E4E4E4', paddingLeft: '5px', borderRadius: '9px', border: '1.5px dashed grey', width: "100%" }}>Existing Data</Typography>
                <Typography
                    variant="h6"
                    sx={{
                        backgroundColor: '#E4E4E4',
                        paddingLeft: '5px',
                        borderRadius: '9px',
                        border: '1.5px dashed grey',
                        width: "100%"
                    }}
                >
                    Enriched Data
                </Typography>
            </div>
            {loading ? (
                <CardsLoadingScreen height="300px" />
            ) : (
                responseData && (
                    <Box className="moderate-profile-container" sx={{ padding: "20px", backgroundColor: '#F0F0F0 !important', overflowY: 'auto', height: 'calc(100vh - 185px)' }}>

                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        backgroundColor: '#E4E4E4',
                                        display: "flex",
                                        alignItems: "center",
                                        padding: "5px",
                                        borderRadius: '10px',
                                        border: '1.5px dashed grey',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => setIsBasicInfoCollapsed(!isBasicInfoCollapsed)}
                                >
                                    <Typography className="work-history-text" sx={{ flexGrow: 1, fontWeight: 500 }}>Basic Info</Typography>
                                    <Box>
                                        {isWorkExperienceCollapsed ? (
                                            <ExpandMore sx={{ maxWidth: '100%', verticalAlign: 'middle' }} />
                                        ) : (
                                            <ExpandLess sx={{ maxWidth: '100%', verticalAlign: 'middle' }} />
                                        )}
                                    </Box>
                                </Typography>
                                <Collapse in={!isBasicInfoCollapsed}>
                                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                                        <Grid item xs={6} sx={{ borderRight: '2px solid #b7b7b7' }}>
                                            <Box>

                                                <Card sx={{
                                                    boxShadow: 3, marginBottom: 2, borderRadius: '25px', border: '2px solid transparent', '&:hover': {
                                                        borderColor: '#EC9324',
                                                    },
                                                    marginRight: 2
                                                }}>
                                                    <CardContent>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                marginBottom: "1rem"
                                                            }}
                                                        >
                                                            <TextField
                                                                label="Name"
                                                                fullWidth
                                                                disabled={true}
                                                                value={localIdentityData?.existingData?.name}
                                                                size="small"
                                                                sx={{
                                                                    flexGrow: 1,
                                                                    marginRight: 1,
                                                                }}
                                                            />
                                                            <IconButton
                                                                sx={{
                                                                    visibility: "hidden",
                                                                    color: isExistingHeadlineEditable ? "#fff" : "#37B498",
                                                                    backgroundColor: isExistingHeadlineEditable
                                                                        ? "#37B498"
                                                                        : "rgba(55, 180, 152, 0.12)",
                                                                    height: "30px",
                                                                    width: "30px",
                                                                    borderRadius: "10px",
                                                                    "&:hover": {
                                                                        backgroundColor: isExistingHeadlineEditable
                                                                            ? "#37B498"
                                                                            : "rgba(55, 180, 152, 0.12)",
                                                                    },
                                                                }}
                                                            >
                                                                <CheckIcon />
                                                            </IconButton>
                                                        </Box>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',

                                                            }}
                                                        >
                                                            <TextField
                                                                label="Headline"
                                                                fullWidth
                                                                disabled={!isExistingHeadlineEditable}
                                                                value={localIdentityData?.existingData?.headline}
                                                                onChange={handleLocalExistingHeadlineChange}
                                                                size="small"
                                                                sx={{
                                                                    flexGrow: 1,
                                                                    marginRight: 1,
                                                                    borderBottom: isExistingHeadlineEditable ? '2px solid #ef9e48' : '2px solid transparent',
                                                                    '& .MuiInputBase-root': {
                                                                        borderRadius: '4px',
                                                                    },
                                                                }}
                                                            />
                                                            <IconButton
                                                                sx={{
                                                                    color: isExistingHeadlineEditable ? "#fff" : "#37B498",
                                                                    backgroundColor: isExistingHeadlineEditable
                                                                        ? "#37B498"
                                                                        : "rgba(55, 180, 152, 0.12)",
                                                                    height: "30px",
                                                                    width: "30px",
                                                                    borderRadius: "10px",
                                                                    "&:hover": {
                                                                        backgroundColor: isExistingHeadlineEditable
                                                                            ? "#37B498"
                                                                            : "rgba(55, 180, 152, 0.12)",
                                                                    },
                                                                }}
                                                                onClick={() => setIsExistingHeadlineEditable(!isExistingHeadlineEditable)}
                                                            >
                                                                <CheckIcon />
                                                            </IconButton>
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>

                                            <Box >
                                                <Card sx={{
                                                    boxShadow: 3, marginBottom: 2, borderRadius: '25px', border: '2px solid transparent', '&:hover': {
                                                        borderColor: '#EC9324',
                                                    },
                                                }}>
                                                    <CardContent>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                marginBottom: "1rem"
                                                            }}
                                                        >
                                                            <TextField
                                                                label="Name"
                                                                fullWidth
                                                                disabled={true}
                                                                value={localIdentityData?.enrichedData?.name}
                                                                size="small"
                                                                sx={{
                                                                    flexGrow: 1,
                                                                    marginRight: 1,
                                                                }}
                                                            />
                                                            <IconButton
                                                                sx={{
                                                                    visibility: "hidden",
                                                                    color: isExistingHeadlineEditable ? "#fff" : "#37B498",
                                                                    backgroundColor: isExistingHeadlineEditable
                                                                        ? "#37B498"
                                                                        : "rgba(55, 180, 152, 0.12)",
                                                                    height: "30px",
                                                                    width: "30px",
                                                                    borderRadius: "10px",
                                                                    "&:hover": {
                                                                        backgroundColor: isExistingHeadlineEditable
                                                                            ? "#37B498"
                                                                            : "rgba(55, 180, 152, 0.12)",
                                                                    },
                                                                }}
                                                            >
                                                                <CheckIcon />
                                                            </IconButton>
                                                        </Box>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',

                                                            }}
                                                        >
                                                            <TextField
                                                                label="Headline"
                                                                fullWidth
                                                                disabled={isExistingHeadlineEditable}
                                                                value={localIdentityData?.enrichedData?.headline}
                                                                onChange={handleLocalEnrichedHeadlineChange}
                                                                size="small"
                                                                sx={{
                                                                    flexGrow: 1,
                                                                    marginRight: 1,
                                                                    borderBottom: !isExistingHeadlineEditable ? '2px solid #ef9e48' : '2px solid transparent',
                                                                    '& .MuiInputBase-root': {
                                                                        borderRadius: '4px',
                                                                    },
                                                                }}
                                                            />
                                                            <IconButton
                                                                sx={{
                                                                    color: !isExistingHeadlineEditable ? "#fff" : "#37B498",
                                                                    backgroundColor: !isExistingHeadlineEditable
                                                                        ? "#37B498"
                                                                        : "rgba(55, 180, 152, 0.12)",
                                                                    height: "30px",
                                                                    width: "30px",
                                                                    borderRadius: "10px",
                                                                    "&:hover": {
                                                                        backgroundColor: !isExistingHeadlineEditable
                                                                            ? "#37B498"
                                                                            : "rgba(55, 180, 152, 0.12)",
                                                                    },
                                                                }}
                                                                onClick={() => setIsExistingHeadlineEditable(!isExistingHeadlineEditable)}
                                                            >
                                                                <CheckIcon />
                                                            </IconButton>
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Collapse>
                            </Grid>

                        </Grid>

                        <Grid container spacing={2} sx={{ marginTop: 0 }}>
                            <Grid item xs={12}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        backgroundColor: '#E4E4E4',
                                        display: "flex",
                                        alignItems: "center",
                                        padding: "5px",
                                        borderRadius: '10px',
                                        border: '1.5px dashed grey',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => setIsWorkExperienceCollapsed(!isWorkExperienceCollapsed)}
                                >
                                    <Typography className="work-history-text" sx={{ flexGrow: 1, fontWeight: 500 }}>Work Experience</Typography>
                                    <Box>
                                        {isWorkExperienceCollapsed ? (
                                            <ExpandMore sx={{ maxWidth: '100%', verticalAlign: 'middle' }} />
                                        ) : (
                                            <ExpandLess sx={{ maxWidth: '100%', verticalAlign: 'middle' }} />
                                        )}
                                    </Box>
                                </Typography>
                                <Collapse in={!isWorkExperienceCollapsed}>
                                    <Grid key={"work-exp"} container sx={{ display: 'flex', marginTop: '0px' }}>
                                        {finalWorkData?.map((work: any, index: number) => {
                                            return (
                                                <div key={index} style={{ display: 'flex', width: "100%" }}>
                                                    <Grid item xs={6} sx={{ paddingRight: 2, borderRight: '2px solid #b7b7b7' }}>
                                                        <Box sx={{ marginTop: 2 }}>
                                                            {renderWorkExperience(work, index, 0)}
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={6} sx={{ paddingLeft: 2 }}>
                                                        <Box sx={{ marginTop: 2 }}>
                                                            {renderWorkExperience(work, index, 1)}
                                                        </Box>
                                                    </Grid>
                                                </div>
                                            )
                                        })}
                                    </Grid>
                                </Collapse>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        backgroundColor: '#E4E4E4',
                                        display: "flex",
                                        alignItems: "center",
                                        padding: "5px",
                                        borderRadius: '10px',
                                        border: '1.5px dashed grey',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => setIsEducationCollapsed(!isEducationCollapsed)}
                                >
                                    <Typography className="education-text" sx={{ flexGrow: 1, fontWeight: 500 }}>Education</Typography>
                                    <Box>
                                        {isEducationCollapsed ? (
                                            <ExpandMore sx={{ maxWidth: '100%', verticalAlign: 'middle' }} />
                                        ) : (
                                            <ExpandLess sx={{ maxWidth: '100%', verticalAlign: 'middle' }} />
                                        )}
                                    </Box>
                                </Typography>
                                <Collapse in={!isEducationCollapsed}>
                                    <Grid container sx={{ display: 'flex', marginTop: '0px' }}>
                                        {finalEducationData?.map((data: any, index: number) => {
                                            return (
                                                <div key={index} style={{ display: 'flex', width: "100%" }}>
                                                    <Grid item xs={6} sx={{ paddingRight: 2, borderRight: '2px solid #b7b7b7' }}>
                                                        <Box sx={{ marginTop: 2 }}>
                                                            {renderEducation(data, index, 0)}
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={6} sx={{ paddingLeft: 2 }}>
                                                        <Box sx={{ marginTop: 2 }}>
                                                            {renderEducation(data, index, 1)}
                                                        </Box>
                                                    </Grid>
                                                </div>
                                            )

                                        })}

                                    </Grid>
                                </Collapse>
                            </Grid>
                        </Grid>
                        <Divider sx={{ marginTop: 2 }} />
                        <Grid className="select-profile-btn" sx={{ marginTop: "20px" }}>
                            <Button className="profile-update-btn" onClick={handleUpdate}>Update</Button>
                        </Grid>
                    </Box>
                )
            )
            }
        </PageLayout>
    );
};

export default ExpertsListPage;
